import { FunctionComponent, useEffect, useState } from "react";
import { ArrowRight, Ban, XCircle } from "lucide-react";

import { useAppDispatch } from "../../../features/helpers";
import { openToast } from "../../../store/ToastSlice";
import { PurchaseService } from "../../../services/PurchaseService";
import { formatAssetAmount } from "../../../utils/decimal";
import { formatCurrencyBRL } from "../../../utils/format";

import { Modal, Alert } from "../../ui";

import "./modal-cancel-purchase.scss";

export interface CancelPurchaseModalProps {
    purchaseId: string;
    onClose: () => void;
    /** Chamado apos o cancelamento ser concluido no backend. */
    onCanceled: () => void;
}

// Confirmacao do cancelamento de uma compra (swap). Mostra todos os dados
// da compra e cada movimento que sera estornado (saldo atual -> saldo apos),
// vindos de GET /purchases/:id/cancel-preview, para o owner ter certeza de
// que e a compra certa antes de confirmar.
const CancelPurchaseModal: FunctionComponent<CancelPurchaseModalProps> = ({ purchaseId, onClose, onCanceled }) => {

    const dispatch = useAppDispatch();

    const [preview, setPreview]     = useState<any | null>(null);
    const [loading, setLoading]     = useState<boolean>(true);
    const [submitting, setSubmit]   = useState<boolean>(false);
    const [confirmed, setConfirmed] = useState<boolean>(false);
    const [error, setError]         = useState<string>("");

    useEffect(() => {
        let alive = true;
        setLoading(true);
        PurchaseService.getCancelPreview(purchaseId)
            .then((data) => { if (alive) setPreview(data); })
            .catch((e: any) => { if (alive) setError(e?.message || "Nao foi possivel carregar os dados da compra."); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [purchaseId]);

    const handleConfirm = async () => {
        setSubmit(true);
        setError("");
        try {
            await PurchaseService.changeStatus(purchaseId, "CANCELED");
            dispatch(openToast({
                title:   "Compra cancelada",
                message: "Os valores foram devolvidos as carteiras de origem.",
                effect:  "success",
            }));
            onCanceled();
            onClose();
        } catch (e: any) {
            setError(e?.message || "Nao foi possivel cancelar a compra. Tente novamente.");
        } finally {
            setSubmit(false);
        }
    };

    const p           = preview?.purchase;
    const movements   = (preview?.movements ?? []) as Array<any>;
    const cancellable = !!preview?.cancellable;
    const hasNegative = movements.some((m) => Number(m.balanceAfter) < 0);

    return (
        <Modal
            open
            onClose={onClose}
            icon={Ban}
            title="Cancelar compra"
            subtitle="todos os movimentos desta compra serao estornados. essa acao nao pode ser desfeita."
            maxWidth={820}
            className="modal-cancel-purchase"
            footer={
                <>
                    <button className="btn-pill ghost" onClick={onClose} disabled={submitting}>
                        Voltar
                    </button>
                    <button
                        className="btn-pill danger"
                        onClick={handleConfirm}
                        disabled={!cancellable || !confirmed || submitting || loading}
                    >
                        {submitting
                            ? <div className="loader"></div>
                            : <><XCircle size={16} /> Cancelar compra</>}
                    </button>
                </>
            }
        >
            {error && <Alert variant="error">{error}</Alert>}

            {loading && <p className="cp-loading">Carregando dados da compra...</p>}

            {p && (
                <>
                    {preview.blockedReason && (
                        <Alert variant="error">{preview.blockedReason}</Alert>
                    )}

                    <section className="cp-section">
                        <h4>Compra</h4>
                        <dl className="cp-grid">
                            <dt>ID</dt>               <dd className="num">{p.id}</dd>
                            <dt>Transacao</dt>        <dd className="num">{p.trx_id}</dd>
                            <dt>Data</dt>             <dd>{new Date(p.createdAt).toLocaleString("pt-BR")}</dd>
                            <dt>Status atual</dt>     <dd>{p.status}</dd>
                            <dt>Cliente</dt>          <dd>{p.client?.name ?? "-"} <small>{p.client?.email ?? ""}</small></dd>
                            <dt>Fornecedor</dt>       <dd>{p.supplier?.name ?? "-"} <small>{p.supplier?.email ?? ""}</small></dd>
                            <dt>Cliente pagou</dt>    <dd className="num">{p.assetOrigin?.symbol} {formatAssetAmount(p.value, p.assetOrigin?.symbol)}</dd>
                            <dt>Cliente recebeu</dt>  <dd className="num">{p.assetTarget?.symbol} {formatAssetAmount(p.targetAmount, p.assetTarget?.symbol)}</dd>
                            <dt>Cotacao venda</dt>    <dd className="num">{formatCurrencyBRL(Number(p.quotation_organization))}</dd>
                            <dt>Cotacao compra</dt>   <dd className="num">{formatCurrencyBRL(Number(p.quotation_real))}</dd>
                            <dt>Saldo (lucro)</dt>    <dd className="num">{p.saldo != null ? formatCurrencyBRL(Number(p.saldo)) : "-"}</dd>
                        </dl>
                    </section>

                    {movements.length > 0 && (
                        <section className="cp-section">
                            <h4>Estorno nas carteiras</h4>
                            <div className="cp-movements">
                                <div className="cp-row head">
                                    <span>Carteira</span>
                                    <span>Estorno</span>
                                    <span>Saldo atual</span>
                                    <span></span>
                                    <span>Saldo apos</span>
                                </div>
                                {movements.map((m, i) => (
                                    <div className="cp-row" key={`${m.walletId}-${i}`}>
                                        <span className="cell-stack">
                                            <strong>{m.ownerName ?? "Organizacao"}</strong>
                                            <small>{m.walletLabel} · {m.assetSymbol}</small>
                                        </span>
                                        <span className={`num ${m.reverseDirection === "IN" ? "delta-up" : "delta-down"}`}>
                                            {m.reverseDirection === "IN" ? "+" : "-"} {formatAssetAmount(m.value, m.assetSymbol)}
                                        </span>
                                        <span className="num">{formatAssetAmount(m.balanceNow, m.assetSymbol)}</span>
                                        <ArrowRight size={14} className="cp-arrow" />
                                        <span className={`num ${Number(m.balanceAfter) < 0 ? "delta-down" : ""}`}>
                                            {formatAssetAmount(m.balanceAfter, m.assetSymbol)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {preview.profits?.length > 0 && (
                        <Alert variant="info">
                            O registro de lucro desta compra ({formatCurrencyBRL(Number(preview.profits[0].profit))}) sera removido das metricas.
                        </Alert>
                    )}

                    {cancellable && hasNegative && (
                        <Alert variant="warning">
                            Algumas carteiras ficarao com saldo negativo apos o estorno (o valor ja foi usado em outras operacoes).
                        </Alert>
                    )}

                    {cancellable && (
                        <label className="cp-confirm">
                            <input
                                type="checkbox"
                                checked={confirmed}
                                onChange={(e) => setConfirmed(e.target.checked)}
                                disabled={submitting}
                            />
                            Conferi os dados e confirmo o cancelamento desta compra.
                        </label>
                    )}
                </>
            )}
        </Modal>
    );
};

export default CancelPurchaseModal;
